import { Link, useNavigate } from "@tanstack/react-router"
import { Button } from "@ui/components/primitives/Button"
import { SegmentedTabs } from "@ui/components/primitives/SegmentedTabs"
import { Rss } from "lucide-react"

import { findBlogAuthor, parseAuthorNames } from "@/lib/blog-authors"
import type { ContentEntry } from "@/lib/content-resolver"
import { RSS_FEED_LINK } from "@/lib/rss-meta"

import {
  getBlogCategories,
  getBlogMeta,
  getPostCategory,
  getPostDescription,
  getPostFeatureImage,
  getPostFeatureImageAlt,
  getPostFeatureImageWebp,
  getPostTitle,
  formatBlogMetaDate,
} from "./blogHelpers"

export function BlogPostMeta({
  post,
  linkAuthors = false,
}: {
  post: ContentEntry
  linkAuthors?: boolean
}) {
  const names = parseAuthorNames(post.frontmatter["author"])
  return (
    <p className="text-site-label font-mono text-muted uppercase" style={{ fontWeight: 500 }}>
      {linkAuthors ? (
        <>
          <time dateTime={post.date}>{formatBlogMetaDate(post.date)}</time>
          {names.length > 0 && " · "}
          {names.map((name, index) => {
            const author = findBlogAuthor(name)
            return (
              <span key={name}>
                {index > 0 && " and "}
                {author ? (
                  <Link
                    to="/blog/authors/$slug"
                    params={{ slug: author.slug }}
                    className="underline decoration-current underline-offset-4 hover:text-default focus-visible:outline-2 focus-visible:outline-offset-4"
                  >
                    {name}
                  </Link>
                ) : (
                  name
                )}
              </span>
            )
          })}
        </>
      ) : (
        getBlogMeta(post)
      )}
    </p>
  )
}

export function BlogCategoryBadge({ category }: { category: string }) {
  return (
    <span className="inline-flex h-6 w-fit items-center rounded bg-neutral-200 px-1.5 text-xs font-normal text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200">
      {category}
    </span>
  )
}

export function BlogFeatureVisual({
  post,
  priority = false,
}: {
  post: ContentEntry
  priority?: boolean
}) {
  const image = getPostFeatureImage(post)

  if (image) {
    return (
      <BlogFeatureImage
        post={post}
        width={1200}
        height={630}
        priority={priority}
        pictureClassName="absolute inset-0 block size-full"
      />
    )
  }

  return (
    <div
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(135deg, rgb(229 229 229 / 0.15) 0%, rgb(229 229 229 / 0.88) 100%)",
      }}
    />
  )
}

export function BlogFeatureImage({
  post,
  width,
  height,
  priority = false,
  pictureClassName,
}: {
  post: ContentEntry
  width: number
  height: number
  priority?: boolean
  pictureClassName?: string
}) {
  const image = getPostFeatureImage(post)
  const webpImage = getPostFeatureImageWebp(post)

  if (!image) {
    return null
  }

  return (
    <picture className={pictureClassName}>
      {webpImage ? <source srcSet={webpImage} type="image/webp" /> : null}
      <img
        src={image}
        alt={getPostFeatureImageAlt(post)}
        width={width}
        height={height}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
        className="size-full object-cover"
      />
    </picture>
  )
}

export function BlogArchiveItem({
  href,
  post,
  onClick,
}: {
  href?: string
  post: ContentEntry
  onClick?: () => void
}) {
  const content = (
    <>
      <div className="flex flex-1 flex-col gap-2">
        <BlogPostMeta post={post} />
        <h2 className="text-site-title font-headline text-default">{getPostTitle(post)}</h2>
        <p className="text-site-description line-clamp-2 font-sans text-muted">
          {getPostDescription(post)}
        </p>
      </div>
      <div className="mt-auto">
        <BlogCategoryBadge category={getPostCategory(post)} />
      </div>
    </>
  )
  const className = "site-hover-surface -mx-4 flex flex-col gap-4 rounded-xl bg-base p-4"
  const ariaLabel = `Read ${getPostTitle(post)}`

  return (
    <article>
      {href ? (
        <a href={href} className={className} aria-label={ariaLabel} onClick={onClick}>
          {content}
        </a>
      ) : (
        <Link
          to="/blog/$slug"
          params={{ slug: post.slug }}
          className={className}
          aria-label={ariaLabel}
          onClick={onClick}
        >
          {content}
        </Link>
      )}
    </article>
  )
}

interface BlogPostCardProps {
  post: ContentEntry
  surface?: "plain" | "muted"
  onClick?: () => void
}

export function BlogPostCard({ post, surface = "plain", onClick }: BlogPostCardProps) {
  return (
    <article
      className={`border-b border-subtle md:border-r md:border-b-0 md:last:border-r-0 ${
        surface === "muted" ? "bg-(--neutral-150) dark:bg-neutral-900" : "bg-base"
      }`}
    >
      <Link
        to="/blog/$slug"
        params={{ slug: post.slug }}
        className="site-hover-surface flex h-full flex-col gap-4 p-6"
        aria-label={`Read ${getPostTitle(post)}`}
        onClick={onClick}
      >
        <div className="flex flex-1 flex-col gap-2">
          <BlogPostMeta post={post} />
          <h2 className="text-site-title font-headline text-default">{getPostTitle(post)}</h2>
          <p className="text-site-description line-clamp-3 font-sans text-muted">
            {getPostDescription(post)}
          </p>
        </div>
        <div className="mt-auto">
          <BlogCategoryBadge category={getPostCategory(post)} />
        </div>
      </Link>
    </article>
  )
}

interface BlogCategoryTabsProps {
  posts: ContentEntry[]
  selectedCategory?: string
}

export function BlogCategoryTabs({ posts, selectedCategory }: BlogCategoryTabsProps) {
  const navigate = useNavigate()
  const categories = getBlogCategories(posts)
  const allArticlesId = "all"
  const selectedValue = selectedCategory ?? allArticlesId

  function handleCategoryChange(value: string) {
    navigate({
      to: "/blog",
      search: value === allArticlesId ? {} : { category: value },
      resetScroll: false,
    })
  }

  return (
    <div className="flex w-full items-start justify-between gap-4">
      <SegmentedTabs
        aria-label="Article categories"
        items={[
          { id: allArticlesId, label: "All articles" },
          ...categories.map((category) => ({ id: category, label: category })),
        ]}
        value={selectedValue}
        onValueChange={handleCategoryChange}
      />
      <Button
        href={RSS_FEED_LINK.href}
        type={RSS_FEED_LINK.type}
        size="icon-small"
        aria-label="RSS feed"
      >
        <Rss className="size-4" />
      </Button>
    </div>
  )
}
